import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import RateAdjustmentModal from './RateAdjustmentModal';

const MyRateRequests = ({ employeeData, onRequestAdjustment }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [whitelistLoading, setWhitelistLoading] = useState(true);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  
  // Comments state
  const [comments, setComments] = useState({});
  const [showComments, setShowComments] = useState({});
  const [newComment, setNewComment] = useState('');
  const [commentingRequestId, setCommentingRequestId] = useState(null);
  
  // Edit & Resubmit state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [currentRates, setCurrentRates] = useState([]);

  useEffect(() => {
    if (employeeData?.employee_number) {
      checkWhitelistStatus();
      fetchRequests();
    }
  }, [employeeData]);

  useEffect(() => {
    // Fetch comments for all requests
    if (requests.length > 0) {
      fetchCommentsForRequests(requests.map(r => r.id));
    }
  }, [requests]);

  const checkWhitelistStatus = async () => {
    try {
      setWhitelistLoading(true);
      const { data, error } = await supabase
        .from('pay_rate_adjustment_whitelist')
        .select('enabled, max_requests_per_year, requests_this_year')
        .eq('employee_number', employeeData.employee_number)
        .single();
      
      if (error) {
        console.log('Employee not whitelisted:', error);
        setIsWhitelisted(false);
        return;
      }
      
      // Check if enabled and hasn't exceeded request limit
      const canRequest = data?.enabled && 
        (data.max_requests_per_year === null || data.requests_this_year < data.max_requests_per_year);
      
      setIsWhitelisted(canRequest);
    } catch (error) {
      console.error('Error checking whitelist:', error);
      setIsWhitelisted(false);
    } finally {
      setWhitelistLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('pay_rate_adjustment_requests')
        .select('*')
        .eq('employee_number', employeeData.employee_number)
        .order('submitted_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching requests:', error);
        return;
      }
      
      setRequests(data || []);
    } catch (error) {
      console.error('Error in fetchRequests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommentsForRequests = async (requestIds) => {
    try {
      const { data, error } = await supabase
        .from('rate_request_comments')
        .select('*')
        .in('request_id', requestIds)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error fetching comments:', error);
        return;
      }
      
      // Group comments by request_id
      const commentsByRequest = {};
      (data || []).forEach(comment => {
        if (!commentsByRequest[comment.request_id]) {
          commentsByRequest[comment.request_id] = [];
        }
        commentsByRequest[comment.request_id].push(comment);
      });
      
      setComments(commentsByRequest);
    } catch (error) {
      console.error('Error in fetchCommentsForRequests:', error);
    }
  };

  const handleAddComment = async (requestId) => {
    if (!newComment.trim()) {
      alert('Please enter a comment');
      return;
    }

    try {
      const { error } = await supabase
        .from('rate_request_comments')
        .insert([{
          request_id: requestId,
          employee_number: employeeData.employee_number,
          comment_text: newComment.trim(),
          is_admin: false
        }]);

      if (error) {
        console.error('Error adding comment:', error);
        alert('Error adding comment: ' + error.message);
        return;
      }

      setNewComment('');
      setCommentingRequestId(null);
      fetchCommentsForRequests([requestId]);
    } catch (error) {
      console.error('Error in handleAddComment:', error);
      alert('Error adding comment. Please try again.');
    }
  };

  const handleEditAndResubmit = async (request) => {
    try {
      // Fetch current rates for the company from the rejected request
      const { data: ratesData, error: ratesError } = await supabase
        .from('employee_pay_rates')
        .select('*')
        .eq('employee_number', employeeData.employee_number)
        .eq('company', request.company)
        .is('end_date', null);

      if (ratesError) {
        console.error('Error fetching rates:', ratesError);
        alert('Error fetching current rates. Please try again.');
        return;
      }

      if (!ratesData || ratesData.length === 0) {
        alert('No current rates found for this company. Please contact admin.');
        return;
      }

      setCurrentRates(ratesData);
      setEditingRequest(request);
      setShowEditModal(true);
    } catch (error) {
      console.error('Error preparing edit:', error);
      alert('Error preparing edit. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'submitted': { 
        bg: 'bg-yellow-100', 
        text: 'text-yellow-800', 
        border: 'border-yellow-300',
        icon: '⏳',
        label: 'Pending Review'
      },
      'under_review': { 
        bg: 'bg-blue-100', 
        text: 'text-blue-800', 
        border: 'border-blue-300',
        icon: '🔍',
        label: 'Under Review'
      },
      'pending_approval': { 
        bg: 'bg-purple-100', 
        text: 'text-purple-800', 
        border: 'border-purple-300',
        icon: '⏰',
        label: 'Pending Approval'
      },
      'approved': { 
        bg: 'bg-green-100', 
        text: 'text-green-800', 
        border: 'border-green-300',
        icon: '✅',
        label: 'Approved'
      },
      'rejected': { 
        bg: 'bg-red-100', 
        text: 'text-red-800', 
        border: 'border-red-300',
        icon: '❌',
        label: 'Rejected'
      },
      'implemented': { 
        bg: 'bg-gray-700', 
        text: 'text-white', 
        border: 'border-gray-800',
        icon: '💰',
        label: 'Implemented'
      }
    };
    
    const config = statusConfig[status] || statusConfig['submitted'];
    
    return (
      <span className={`${config.bg} ${config.text} ${config.border} border px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1`}>
        <span>{config.icon}</span>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const filteredRequests = filterStatus === 'all' 
    ? requests 
    : requests.filter(r => r.status === filterStatus);

  // Don't render anything if not whitelisted (wait for whitelist check to complete)
  if (!whitelistLoading && !isWhitelisted) {
    return null;
  }

  if (loading || whitelistLoading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="text-3xl">📊</span>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">My Rate Requests</h2>
            <p className="text-sm text-gray-500">{requests.length} total request{requests.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={fetchRequests}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-semibold"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Filter Tabs */}
      {requests.length > 0 && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors ${
              filterStatus === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({requests.length})
          </button>
          <button
            onClick={() => setFilterStatus('submitted')}
            className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors ${
              filterStatus === 'submitted'
                ? 'bg-yellow-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pending ({requests.filter(r => r.status === 'submitted' || r.status === 'under_review').length})
          </button>
          <button
            onClick={() => setFilterStatus('approved')}
            className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors ${
              filterStatus === 'approved'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Approved ({requests.filter(r => r.status === 'approved' || r.status === 'implemented').length})
          </button>
          <button
            onClick={() => setFilterStatus('rejected')}
            className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors ${
              filterStatus === 'rejected'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Rejected ({requests.filter(r => r.status === 'rejected').length})
          </button>
        </div>
      )}

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📋</div>
          <p className="text-gray-500 text-lg">
            {filterStatus === 'all' 
              ? 'No rate adjustment requests yet' 
              : `No ${filterStatus} requests`}
          </p>
          {filterStatus !== 'all' && (
            <button
              onClick={() => setFilterStatus('all')}
              className="mt-4 text-blue-600 hover:text-blue-700 font-semibold"
            >
              View all requests
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div
              key={request.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedRequest(selectedRequest?.id === request.id ? null : request)}
            >
              {/* Request Header */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-lg text-gray-800">{request.request_no}</h3>
                    {getStatusBadge(request.status)}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>📅 {formatDate(request.submitted_at)}</span>
                    <span>🏢 {request.company}</span>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg
                    className={`w-5 h-5 transform transition-transform ${
                      selectedRequest?.id === request.id ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Quick Summary */}
              <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
                <div className="bg-blue-50 rounded p-2">
                  <p className="text-xs text-gray-500">Check Rate</p>
                  <p className="font-semibold text-gray-800">
                    {formatCurrency(request.current_check_rate)} → {formatCurrency(request.requested_check_rate)}
                  </p>
                  {request.check_rate_increase > 0 && (
                    <p className="text-xs text-green-600 font-semibold">
                      +{formatCurrency(request.check_rate_increase)} ({request.check_rate_increase_pct?.toFixed(1)}%)
                    </p>
                  )}
                </div>
                <div className="bg-green-50 rounded p-2">
                  <p className="text-xs text-gray-500">Weekday Cash</p>
                  <p className="font-semibold text-gray-800">
                    {formatCurrency(request.current_weekday_cash_rate)} → {formatCurrency(request.requested_weekday_cash_rate)}
                  </p>
                  {request.weekday_cash_rate_increase > 0 && (
                    <p className="text-xs text-green-600 font-semibold">
                      +{formatCurrency(request.weekday_cash_rate_increase)} ({request.weekday_cash_increase_pct?.toFixed(1)}%)
                    </p>
                  )}
                </div>
                <div className="bg-purple-50 rounded p-2">
                  <p className="text-xs text-gray-500">Weekend Cash</p>
                  <p className="font-semibold text-gray-800">
                    {formatCurrency(request.current_weekend_cash_rate)} → {formatCurrency(request.requested_weekend_cash_rate)}
                  </p>
                  {request.weekend_cash_rate_increase > 0 && (
                    <p className="text-xs text-green-600 font-semibold">
                      +{formatCurrency(request.weekend_cash_rate_increase)} ({request.weekend_cash_increase_pct?.toFixed(1)}%)
                    </p>
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {selectedRequest?.id === request.id && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                  {/* Reason */}
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Reason</p>
                    <p className="text-sm text-gray-700">{request.reason}</p>
                  </div>

                  {/* Justification */}
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Justification</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                      {request.justification}
                    </p>
                  </div>

                  {/* Achievements */}
                  {request.achievements && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Achievements</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                        {request.achievements}
                      </p>
                    </div>
                  )}

                  {/* Requested Effective Date */}
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Requested Effective Date</p>
                    <p className="text-sm text-gray-700">{formatDate(request.requested_effective_date)}</p>
                  </div>

                  {/* Approval/Rejection Details */}
                  {request.status === 'approved' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="font-semibold text-green-800 mb-2">✅ Approved</p>
                      {request.final_approval_notes && (
                        <div className="mb-2">
                          <p className="text-xs text-gray-600 mb-1">Admin Notes:</p>
                          <p className="text-sm text-gray-700">{request.final_approval_notes}</p>
                        </div>
                      )}
                      {request.approved_effective_date && (
                        <p className="text-sm text-gray-600">
                          Effective Date: <span className="font-semibold">{formatDate(request.approved_effective_date)}</span>
                        </p>
                      )}
                      {request.final_approved_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          Approved on {formatDate(request.final_approved_at)}
                        </p>
                      )}
                    </div>
                  )}

                  {request.status === 'rejected' && request.rejection_reason && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="font-semibold text-red-800 mb-2">❌ Rejected</p>
                      <p className="text-sm text-gray-700 mb-3">{request.rejection_reason}</p>
                      {request.rejected_at && (
                        <p className="text-xs text-gray-500 mb-3">
                          Rejected on {formatDate(request.rejected_at)}
                        </p>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditAndResubmit(request);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-semibold"
                      >
                        ✏️ Edit & Resubmit
                      </button>
                    </div>
                  )}

                  {request.status === 'implemented' && (
                    <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                      <p className="font-semibold text-gray-800 mb-2">💰 Implemented</p>
                      <p className="text-sm text-gray-700">
                        Your new pay rate is now active and will be reflected in your next paycheck.
                      </p>
                      {request.implemented_at && (
                        <p className="text-xs text-gray-500 mt-2">
                          Implemented on {formatDate(request.implemented_at)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Comments Section */}
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700">Comments ({(comments[request.id] || []).length})</h4>
                      <button
                        onClick={() => setShowComments({ ...showComments, [request.id]: !showComments[request.id] })}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {showComments[request.id] ? 'Hide' : 'Show'} Comments
                      </button>
                    </div>
                    
                    {showComments[request.id] && (
                      <>
                        <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                          {(comments[request.id] || []).map((comment) => (
                            <div key={comment.id} className={`text-xs p-2 rounded ${comment.is_admin ? 'bg-blue-50 border-l-2 border-blue-500' : 'bg-gray-50 border-l-2 border-gray-300'}`}>
                              <div className="flex justify-between items-start mb-1">
                                <span className="font-semibold text-gray-700">
                                  {comment.is_admin ? '👤 Admin' : `👤 ${comment.employee_number}`}
                                </span>
                                <span className="text-gray-500">
                                  {new Date(comment.created_at).toLocaleDateString()} {new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              <p className="text-gray-700">{comment.comment_text}</p>
                            </div>
                          ))}
                          {(comments[request.id] || []).length === 0 && (
                            <p className="text-xs text-gray-500 text-center py-2">No comments yet</p>
                          )}
                        </div>
                        
                        {commentingRequestId === request.id ? (
                          <div>
                            <textarea
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder="Add a comment..."
                              rows="2"
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <div className="flex gap-2 mt-1">
                              <button
                                onClick={() => handleAddComment(request.id)}
                                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                Post
                              </button>
                              <button
                                onClick={() => {
                                  setCommentingRequestId(null);
                                  setNewComment('');
                                }}
                                className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setCommentingRequestId(request.id)}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            + Add Comment
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit & Resubmit Modal */}
      {showEditModal && editingRequest && currentRates.length > 0 && (
        <RateAdjustmentModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingRequest(null);
            setCurrentRates([]);
          }}
          employeeData={employeeData}
          currentRates={currentRates}
          existingRequest={editingRequest}
          onSubmitSuccess={() => {
            setShowEditModal(false);
            setEditingRequest(null);
            setCurrentRates([]);
            fetchRequests(); // Refresh the list
          }}
        />
      )}
    </div>
  );
};

export default MyRateRequests;

